import "../css/pages/Login.css";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";
import SecuritySeal from "../components/SecuritySeal";

const RECAPTCHA_SITE_KEY = "6LfxnBstAAAAAIQRiGw0TSIQsXfDIo0W1Ewgu3Dd";
const IS_LOCAL =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export default function Login() {
  const { user, login, verifyOtpAndLogin } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  const [step, setStep] = useState(1); // 1 = Email/Password/Captcha, 2 = 2FA OTP
  const [form, setForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(
    IS_LOCAL ? "local-bypass" : null,
  );

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (error) setError("");
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  // 60-second Countdown Timer for Resend OTP
  useEffect(() => {
    let interval = null;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Step 1: Submit Credentials & CAPTCHA -> Trigger OTP
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!form.email.trim()) {
      setError("Email address is required.");
      return;
    }
    if (!form.password) {
      setError("Password is required.");
      return;
    }
    if (!IS_LOCAL && !captchaToken) {
      setError("Please complete the CAPTCHA verification.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Invalid email or password. Please try again.";
      setError(msg);
      if (!IS_LOCAL) {
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit 6-Digit OTP Code
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!otp || otp.trim().length !== 6) {
      setError("Enter the valid 6-digit OTP code.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await verifyOtpAndLogin(form.email, otp.trim());
      toast.success("✅ 2FA Verification Successful! Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Code
  const handleResendOtp = async () => {
    if (timer > 0) return;
    try {
      await login(form.email, form.password);
      setTimer(60);
      toast.success("🔑 New 2FA Security OTP code sent!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  return (
    <div className="login-page">
      {/* Left hero panel */}
      <div className="login-left">
        <div className="login-hero">
          <div className="brand">
            <div className="brand-icon">$</div>
            <div className="brand-name">
              Secure<span>Check</span>
            </div>
          </div>
          <div className="login-hero-text">
            <h1>
              Check Cashing
              <br />
              Made Simple.
            </h1>
            <p>
              A complete point-of-sale system for check cashing stores. Manage
              customers, track transactions, and grow your business.
            </p>
            <div className="login-features">
              {[
                "Customer profile management with IDs & contacts",
                "Real-time check cashing & loan tracking",
                "256-Bit Encryption & 2FA Security Protection",
              ].map((f, i) => (
                <div className="login-feature" key={i}>
                  <div className="check">✓</div>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="login-right">
        <div className="login-form-wrap">
          {step === 1 ? (
            <>
              <h2>Sign in to your account</h2>
              <p>Enter your credentials to access the dashboard</p>

              {error && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1.5px solid #fca5a5",
                    borderRadius: 10,
                    padding: "11px 14px",
                    marginBottom: 18,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 15, flexShrink: 0 }}>🚫</span>
                  <span
                    style={{
                      fontSize: 13.5,
                      color: "#b91c1c",
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {error}
                  </span>
                  <button
                    type="button"
                    onClick={() => setError("")}
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#b91c1c",
                      fontSize: 16,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={handleStep1Submit} noValidate>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="username"
                    style={{
                      ...(error && !form.email ? { borderColor: "#fca5a5" } : {}),
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPass ? "text" : "password"}
                      name="password"
                      className="form-control"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                      style={{
                        paddingRight: 44,
                        ...(error && !form.password
                          ? { borderColor: "#fca5a5" }
                          : {}),
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--gray-1)",
                        fontSize: 16,
                      }}
                    >
                      {showPass ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>

                {/* ── Google reCAPTCHA v2 — hidden on localhost ── */}
                {!IS_LOCAL && (
                  <div
                    style={{
                      margin: "20px 0 18px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                      onExpired={handleCaptchaExpired}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || !captchaToken}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                      }}
                    >
                      <span className="spinner" /> Verifying Credentials...
                    </span>
                  ) : (
                    "Sign In →"
                  )}
                </button>
              </form>
            </>
          ) : (
            /* ── STEP 2: 2FA OTP VERIFICATION FORM ── */
            <>
              <h2>🔑 Enter 2FA Security Code</h2>
              <p>
                A 6-digit OTP code has been sent to <b>{form.email}</b>.
              </p>

              {error && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1.5px solid #fca5a5",
                    borderRadius: 10,
                    padding: "11px 14px",
                    marginBottom: 18,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 15, flexShrink: 0 }}>🚫</span>
                  <span
                    style={{
                      fontSize: 13.5,
                      color: "#b91c1c",
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {error}
                  </span>
                  <button
                    type="button"
                    onClick={() => setError("")}
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#b91c1c",
                      fontSize: 16,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={handleStep2Submit} noValidate>
                <div className="form-group" style={{ textAlign: "center" }}>
                  <label className="form-label" style={{ marginBottom: 8, fontSize: 13 }}>
                    6-DIGIT OTP SECURITY CODE
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    className="form-control"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/[^0-9]/g, ""));
                      if (error) setError("");
                    }}
                    style={{
                      fontSize: 24,
                      letterSpacing: 8,
                      textAlign: "center",
                      fontWeight: 800,
                      color: "#1e293b",
                    }}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || otp.length !== 6}
                  style={{ marginTop: 16 }}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                      }}
                    >
                      <span className="spinner" /> Verifying Code...
                    </span>
                  ) : (
                    "Verify OTP & Enter →"
                  )}
                </button>

                {/* Resend & Back buttons */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 18,
                    fontSize: 12.5,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setError("");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#64748b",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    ← Back to Login
                  </button>

                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={timer > 0}
                    style={{
                      background: "none",
                      border: "none",
                      color: timer > 0 ? "#94a3b8" : "#10b981",
                      cursor: timer > 0 ? "default" : "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {timer > 0 ? `Resend Code in ${timer}s` : "Resend OTP Code"}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Security & Compliance Seal ── */}
          <SecuritySeal style={{ marginTop: 24 }} />
        </div>
      </div>
    </div>
  );
}
